import Router, { useRouter } from "next/router";
import Slider from "rc-slider";
import React, { ComponentProps } from "react";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { SearchQuery } from "shared/data/reducers/types/storage";

import PriceFormatter from "@components/Formatters/PriceFormatter";
import FormattedMessage from "@components/UI/FormattedMessage";

const { createSliderWithTooltip } = Slider;
const Range = createSliderWithTooltip(Slider.Range);

type Props = {
  query: SearchQuery;
  lowestPrice: number;
  highestPrice: number;
  title: ComponentProps<typeof FormattedMessage>["id"];
  subTitle: ComponentProps<typeof FormattedMessage>["id"];
  lowestPriceQueryParam?: keyof SearchQuery;
  highestPriceQueryParam?: keyof SearchQuery;
};

const FilterBoxPrice = ({
  query,
  lowestPrice,
  highestPrice,
  title,
  subTitle,
  lowestPriceQueryParam = "lowestPrice",
  highestPriceQueryParam = "highestPrice",
}: Props) => {
  const [filteredPrice, setFilteredPrice] = React.useState<number[]>([
    Number(query[lowestPriceQueryParam]) || lowestPrice,
    Number(query[highestPriceQueryParam]) || highestPrice,
  ]);

  React.useEffect(() => {
    setFilteredPrice([
      Number(query[lowestPriceQueryParam]) || lowestPrice,
      Number(query[highestPriceQueryParam]) || highestPrice,
    ]);
  }, [
    query,
    lowestPriceQueryParam,
    highestPriceQueryParam,
    lowestPrice,
    highestPrice,
  ]);

  const { currency } = useSelector((appState: AppState) => ({
    currency: appState.search.currency,
  }));
  const router = useRouter();

  const setPriceFilter = (range: number[]) => {
    setFilteredPrice(range);
  };

  const onAfterChange = (value: [number, number]) => {
    const { pathname } = router;
    const newQuery = {
      ...query,
      [lowestPriceQueryParam]: value[0],
      [highestPriceQueryParam]: value[1],
    };
    Router.push(
      {
        pathname,
        query: newQuery,
      },
      undefined,
      { scroll: false }
    );
  };

  return (
    <div className="filters-box">
      <span className="filters-label">
        <FormattedMessage id={title} />:
      </span>
      <div className="filters-box-content">
        <div className="filters-range-description">
          <b>
            <FormattedMessage id={subTitle} />:{" "}
            <span style={{ float: "right" }}>
              <PriceFormatter
                price={filteredPrice[0]}
                customCurrency={router.query.currency || currency}
              />{" "}
              -{" "}
              <PriceFormatter
                price={filteredPrice[1]}
                customCurrency={router.query.currency || currency}
              />
            </span>
          </b>
        </div>

        <Range
          onAfterChange={onAfterChange}
          onChange={(e) => {
            setPriceFilter([e[0], e[1]]);
          }}
          min={lowestPrice}
          step={1}
          allowCross={false}
          max={highestPrice}
          value={filteredPrice}
          tipFormatter={(val) => (
            <PriceFormatter
              price={val}
              customCurrency={router.query.currency || currency}
            />
          )}
        />
      </div>
    </div>
  );
};

export default FilterBoxPrice;
